/** /itineraries/ — cross-tenant itinerary-catalog packages (Itinerary_Group).
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). An "itinerary" here is the catalog PACKAGE
 * (Itinerary_Group) + its nested `itinerary_default` catalog fields (pricing,
 * currencies, highlights) + day-level titles. The funnel deliberately ignores
 * trip-flow slots/activities (`models.py:26178`) — days carry NO timeslots.
 *
 * Reads, create and delete; an edit is the itinerary changeset
 * (`itineraryRecord.ts`) — the console never PATCHes an itinerary. A blocked
 * delete is 409 `itinerary_in_use`.
 *
 * GOTCHAS:
 *  - `cost` is a decimal STRING on the wire (e.g. "199.50").
 *  - `day_count` is read-only (derived from the number of days) — never send it.
 */
import type { CatalogSource } from './activities';
import { apiDelete, apiGet, apiPost } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { components } from './types';
import {
  fetchUsedBy,
  type UsedByParams,
  type UsedByRef,
  type UsedByResponse,
  type UsedByTypeOption,
} from './usedBy';
import type { PaginatedResponse } from './users';
import type { ImageChunks } from '@/utils/imageUrl';
import type { TenantIdsParam } from '@/scope/tenantIds';

// ---------------------------------------------------------------------------
// Enums (mirror the backend serializer/funnel; integer-valued on the wire)
// ---------------------------------------------------------------------------

/** `cta_button_behavior` — what the call-to-action button DOES in the app
 *  (`Tour_Operator.CTA_BEHAVIORS`). Labels mirror the spec's `x-enum-labels`
 *  on `ConsoleItineraryDetail.cta_button_behavior`; `itineraries.contract.test.ts`
 *  asserts the equality, because this map previously held the *departure*
 *  labels below and silently hid behaviors 3-6 from the console. */
export const CTA_BUTTON_BEHAVIOR = {
  LINK_INAPP: 0,
  LINK_BROWSER: 1,
  PHONE: 2,
  CHAT: 3,
  ADD_TRIP: 4,
  EXPLORE: 5,
  ADD_OR_EXPLORE: 6,
} as const;
export const CTA_BUTTON_BEHAVIOR_LABELS: Record<number, string> = {
  0: 'Link via In-App Browser',
  1: 'Link via default browser',
  2: 'Phone Number',
  3: 'Assistant Chat',
  4: 'Add to My Trips',
  5: 'Show on Explore',
  6: 'Add to My Trips/Show on Explore',
};

/** `cta_tour_departure` — which departure of `cta_tour` the button opens
 *  (`Itinerary_Group.CTA_TOUR_DEPARTURES`). Non-nullable server-side, default 0. */
export const CTA_TOUR_DEPARTURE = { NEXT: 0, RECENT: 1, ASK: 2 } as const;
export const CTA_TOUR_DEPARTURE_LABELS: Record<number, string> = {
  0: 'Next available',
  1: 'Most recent',
  2: 'Ask user',
};

// --- CTA field dependencies -------------------------------------------------
// Which secondary fields a behavior actually uses. The dashboard's itinerary
// editor (explore/ItineraryEdit.js:763/787/824) is the reference; these take the
// editor draft's STRING select value so no caller hand-rolls a `+coercion`
// comparison — the bug class that makes the dashboard's own `=== 2` checks dead
// code. A blank value ("inherit the operator default") uses none of them.

const behaviorIs = (v: string, ...vals: number[]): boolean =>
  v !== '' && vals.includes(Number(v));

/** The two link behaviors open the Booking URL, so they require one. */
export const ctaNeedsBookingUrl = (behavior: string): boolean =>
  behaviorIs(behavior, CTA_BUTTON_BEHAVIOR.LINK_INAPP, CTA_BUTTON_BEHAVIOR.LINK_BROWSER);

/** The behaviors that read the Booking URL: the two links open it, and Phone
 *  dials it — it holds a `tel:` link (the XML import stores `tel:<number>`). */
export const ctaUsesBookingUrl = (behavior: string): boolean =>
  ctaNeedsBookingUrl(behavior) || behaviorIs(behavior, CTA_BUTTON_BEHAVIOR.PHONE);

/** A target tour applies to the three my-trips/explore behaviors. */
export const ctaNeedsTour = (behavior: string): boolean =>
  behaviorIs(
    behavior,
    CTA_BUTTON_BEHAVIOR.ADD_TRIP,
    CTA_BUTTON_BEHAVIOR.EXPLORE,
    CTA_BUTTON_BEHAVIOR.ADD_OR_EXPLORE,
  );

/** A target departure applies only where the button ADDS a trip — "Show on
 *  Explore" (5) needs the tour but never a departure. */
export const ctaNeedsDeparture = (behavior: string): boolean =>
  behaviorIs(behavior, CTA_BUTTON_BEHAVIOR.ADD_TRIP, CTA_BUTTON_BEHAVIOR.ADD_OR_EXPLORE);

// ---------------------------------------------------------------------------
// Read shapes
// ---------------------------------------------------------------------------

/** A currency ref (matches `@/api/pickers` `Currency`). */
export interface CurrencyRef {
  id: number;
  code: string;
  name: string;
  symbol: string;
}

/** A day-level title row (day metadata only — NO timeslots). */
export interface ItineraryDay {
  id: number;
  sequence: number;
  title: string | null;
  subtitle: string | null;
  /** HTML. */
  description: string | null;
  /** HTML. */
  footer: string | null;
  day_span: number;
  display_type: number;
  image: ImageChunks | null;
}

export interface ItineraryListItem {
  id: number;
  /** Owning tenant; null for unassigned/orphaned itineraries (`Itinerary_Group.tour_operator`
   *  is nullable). The list grid renders null as '—'. */
  tenant: TenantRef | null;
  name: string | null;
  published: boolean;
  /** Derived from the number of days (read-only). */
  day_count: number | null;
  /** Per-person price override — decimal string, e.g. "199.50" (visible). */
  cost: string | null;
  currency: CurrencyRef | null;
  /** Hidden by default. */
  cost_prefix: string | null;
  cost_unit: string | null;
  booking_url: string | null;
  duration_display: string | null;
  sequence: number;
  updated_ts: string | null;
  /** Catalog markers (were detail-only). */
  has_cover_image: boolean;
  show_in_catalog: boolean;
  /** Operator's external id — also matched by the basic search. */
  custom_id: string | null;
  /** The operator's placeholder that new tours start from; never deletable (409
   *  `itinerary_placeholder`). */
  placeholder: boolean;
  /** `CTA_BUTTON_BEHAVIOR` (0-6). Null inherits the operator-level behavior. */
  cta_button_behavior: number | null;
  /** No catalog itinerary (day plan) yet: the app shows "Coming soon". */
  default_missing: boolean;
  /** The import source of the newest import that touched it (its name); null = created in
   *  the console. Not orderable. */
  source_name: string | null;
}

/** What depends on the itinerary, counted the way its delete guard counts
 *  (`ItineraryDependents`), each read bounded: `trips_added` stops at 1,000 and
 *  `trips_added_capped` means more. */
export interface ItineraryUsage {
  /** Tours cross-selling it. */
  crosssold_on: number;
  /** Other itineraries showing its days (= `default_itinerary.shared_with`). */
  sharing_groups: number;
  /** Images in its gallery (the default itinerary's). */
  image_count: number;
  /** Tours whose own itinerary was built from it (deleted tours included). */
  tour_itineraries: number;
  /** Travellers' trips added from it (no tour), at most 1,000. */
  trips_added: number;
  /** More than 1,000 trips: `trips_added` reads "1,000+". */
  trips_added_capped: boolean;
}

/** The catalog itinerary (day plan) the group shows — `id` null: none yet, the app shows
 *  "Coming soon" — and the other groups showing the same day plan (an edit of its days or
 *  fields changes them too). */
export interface ItineraryDefault {
  id: number | null;
  /** Group ids. */
  shared_with: number[];
}

export interface ItineraryDetail extends ItineraryListItem {
  description: string | null;
  /** HTML. */
  footer: string | null;
  custom_day_titles: boolean;
  // show_in_catalog / custom_id / cta_button_behavior are inherited from ItineraryListItem.
  show_in_trip_type_search: boolean;
  show_in_region_search: boolean;
  show_in_text_search: boolean;
  cta_button_text: string | null;
  cta_tour: NamedRef | null;
  /** `CTA_TOUR_DEPARTURE` (0-2). Non-nullable server-side; the null tolerates a
   *  record written before the column existed. */
  cta_tour_departure: number | null;
  atag: NamedRef[];
  /** 1 TO-default / 2 USD / 3 local / 4 device. */
  catalog_display_currency: number | null;
  ontrip_display_currency: number | null;
  /** HTML. */
  highlights: string | null;
  /** HTML. */
  notes: string | null;
  /** HTML. */
  packing_tips: string | null;
  route_map: ImageChunks | null;
  regions: NamedRef[];
  trip_types: NamedRef[];
  tags: NamedRef[];
  /** Month numbers 1-12. */
  months: number[];
  days: ItineraryDay[];
  // ---- the record's fields (plan §5, §9.5) ----
  /** The import identity (a UUID derived from `custom_id`; read-only). */
  ext_id: string | null;
  /** The itinerary in the operator's dashboard; null without an operator. */
  dashboard_url: string | null;
  source: CatalogSource | null;
  usage: ItineraryUsage;
  default_itinerary: ItineraryDefault;
  /** The operator's `cta_button_behavior`: what the button does while this one's is null
   *  (inherit). Null without an operator. */
  operator_cta_default: number | null;
  /** The Branch.io deep link. */
  branchio_link: string | null;
  /** The deep link's STORED QR image on the media CDN — never generated on read: null until
   *  the Regenerate deep link action makes one. */
  qr_image_url: string | null;
  /** The line set (Vehicle_Route_Set) the itinerary runs on. */
  vehicle_route_set: NamedRef | null;
  /** The cover the app shows: the default itinerary's first gallery image (not
   *  `has_cover_image`, the legacy cover column). */
  cover_image: ImageChunks | null;
}

// ---------------------------------------------------------------------------
// Create shape
// ---------------------------------------------------------------------------

/** POST-create body: `tour_operator` required, name optional (the funnel mints a
 *  stub group + itinerary). The console sends a name-only shell; the rest is
 *  filled in on the detail editor. */
export interface ItineraryCreateInput {
  tour_operator: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Params + query
// ---------------------------------------------------------------------------

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  published?: 'true' | 'false';
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.published) usp.set('published', params.published);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

export function list(params: ListParams = {}): Promise<PaginatedResponse<ItineraryListItem>> {
  return apiGet<PaginatedResponse<ItineraryListItem>>(`/itineraries/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<ItineraryDetail> {
  return apiGet<ItineraryDetail>(`/itineraries/${id}/`);
}

/** POST /itineraries/ — the console always creates a name-only shell (plain
 *  JSON); the rest is edited on the record. */
export function create(body: ItineraryCreateInput): Promise<ItineraryDetail> {
  return apiPost<ItineraryDetail>('/itineraries/', body);
}

/** What depends on an itinerary (`ConsoleItineraryDependents`), counted only when a
 *  delete is refused. `trips_added` stops at 1,000; `trips_added_capped` means more. */
export interface ItineraryDependents {
  tour_itineraries: number;
  trips_added: number;
  trips_added_capped: boolean;
  template_tours: number;
  sharing_groups: number;
  crosssell_tours: number;
}

/** The DELETE's 409 body (`ConsoleItineraryDeleteConflict`); nothing was deleted.
 *  `dependents` is absent only when the legacy protected-reference fallback refused. */
export interface ItineraryDeleteConflict {
  detail: string;
  code: 'itinerary_in_use' | 'itinerary_placeholder';
  dependents?: ItineraryDependents;
}

/** One itinerary a batch `delete` left out (`ConsoleItineraryBatchDeleteRefusal`), with the
 *  code the single DELETE refuses it with. */
export interface ItineraryBatchDeleteRefusal {
  id: number;
  code: ItineraryDeleteConflict['code'];
}

/** The batch `delete`'s 409 body (`ConsoleItineraryBatchDeleteRefused`): every selected
 *  itinerary was refused and nothing was dispatched. When only some are, the 202 counts the
 *  rest in `processed` and the task's `params.op_params.refused` lists the refused ones. */
export interface ItineraryBatchDeleteRefused {
  detail: string;
  code: 'itinerary_delete_refused';
  refused: ItineraryBatchDeleteRefusal[];
}

/** Rejects with a 409 `ItineraryDeleteConflict` for a placeholder or an itinerary in use. */
export function remove(id: number): Promise<void> {
  return apiDelete(`/itineraries/${id}/`);
}

// ---------------------------------------------------------------------------
// Used-by (GET /itineraries/{id}/used_by/) — what the delete guard counts, listed
// ---------------------------------------------------------------------------

/** The itinerary used-by's groups, one `type_key` each (the spec's enum, which is also the
 *  order the endpoint runs them in): every tour that depends on it (once each, `relations`
 *  says how); the other itineraries showing its days; travellers' trips added from it —
 *  newest first while there are at most 1,000, above that counted only (`count_only`,
 *  `capped`). */
export const ITINERARY_USED_BY_TYPES = [
  'demo_group_tour',
  'demo_itinerary_group',
  'demo_user_itinerary',
] as const;
export type ItineraryUsedByType = (typeof ITINERARY_USED_BY_TYPES)[number];

/** How a tour row depends on the itinerary: the delete guard's `dependents` keys. */
export type ItineraryTourRelation = Extract<
  keyof ItineraryDependents,
  'tour_itineraries' | 'template_tours' | 'crosssell_tours'
>;

/** The order a tour row's `relations` run in. */
export const ITINERARY_TOUR_RELATIONS: readonly ItineraryTourRelation[] = [
  'tour_itineraries',
  'template_tours',
  'crosssell_tours',
];

/** One referencing record; the extra fields by type. */
export interface ItineraryUsedByRef extends UsedByRef {
  /** `demo_group_tour`: how the tour depends on it, in `ITINERARY_TOUR_RELATIONS` order. */
  relations?: ItineraryTourRelation[];
  /** `demo_group_tour`: a deleted tour (a delete would still take its itinerary). */
  deleted?: boolean;
  /** `demo_user_itinerary`: when the traveller added the trip (ISO). */
  created_ts?: string | null;
  /** `demo_user_itinerary`: the trip's start date (`YYYY-MM-DD`). */
  travel_date?: string | null;
  /** `demo_user_itinerary`: the traveller (users.User id). */
  user_id?: number;
}

export interface ItineraryUsedByTypeOption extends UsedByTypeOption {
  key: ItineraryUsedByType;
  /** The type's total rows before `search` / `type`; trips at most 1,000 (`capped`). */
  count: number;
}

/** The shared used-by envelope over what the itinerary's delete guard counts; the counts
 *  agree with its `dependents`. */
export interface ItineraryUsedByList extends UsedByResponse {
  results: ItineraryUsedByRef[];
  available_types: ItineraryUsedByTypeOption[];
}

/** One page of where the itinerary is used: rows type by type, then by name; `type` narrows
 *  them to one group. */
export function usedBy(
  id: number,
  params: Omit<UsedByParams, 'type'> & { type?: ItineraryUsedByType } = {},
): Promise<ItineraryUsedByList> {
  return fetchUsedBy<ItineraryUsedByList>('itineraries', id, params);
}

// ---------------------------------------------------------------------------
// Used-by summary (GET /itineraries/{id}/used_by/summary/)
// ---------------------------------------------------------------------------

/** The live tours using the itinerary as their template or built on it (`tours`, once each,
 *  at most 500 — `capped` above, with no departures counted; `template_tours` of them) and
 *  their upcoming departures and bookings; `available: false` + `reason` when the database
 *  cancelled the read (every count null). Remembered 10 minutes (2 when unavailable);
 *  `computed_at` says when it was read (plan §9.5, D7). */
export type ItineraryUsedBySummary = components['schemas']['ConsoleItineraryUsedBySummary'];

/** The itinerary's used-by summary — lazy: the record's band reads it after the detail,
 *  which never computes it. */
export function usedBySummary(id: number): Promise<ItineraryUsedBySummary> {
  return apiGet<ItineraryUsedBySummary>(`/itineraries/${id}/used_by/summary/`);
}
